import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';

interface PostCardSkeletonProps {
  /** Reserves space for the media block, so tall cards do not jump on load. */
  withMedia?: boolean;
}

/**
 * Spec §9.3: skeletons on every async surface, never a spinner on the feed.
 *
 * The shape deliberately mirrors PostCard — including the 4px accent bar — so
 * the swap to real content moves nothing.
 */
export function PostCardSkeleton({ withMedia = false }: PostCardSkeletonProps) {
  return (
    <Card sx={{ display: 'flex', overflow: 'hidden' }} aria-hidden>
      <Box sx={{ width: 4, flexShrink: 0, backgroundColor: 'divider' }} />

      <Stack spacing={1.5} sx={{ p: 2, flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Skeleton variant="circular" width={36} height={36} />
          <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="42%" height={16} />
            <Skeleton variant="text" width="28%" height={13} />
          </Stack>
          <Skeleton variant="rounded" width={72} height={28} />
        </Stack>

        <Skeleton variant="text" width="80%" height={22} />

        <Box>
          <Skeleton variant="text" height={16} />
          <Skeleton variant="text" height={16} />
          <Skeleton variant="text" width="64%" height={16} />
        </Box>

        {withMedia && <Skeleton variant="rounded" height={220} />}

        <Stack direction="row" spacing={2} sx={{ pt: 0.5 }}>
          <Skeleton variant="rounded" width={64} height={32} />
          <Skeleton variant="rounded" width={64} height={32} />
          <Skeleton variant="rounded" width={40} height={32} />
        </Stack>
      </Stack>
    </Card>
  );
}

/** A run of cards for the initial feed load. */
export function FeedSkeleton({ count = 4 }: { count?: number }) {
  const { t } = useTranslation();

  return (
    <Stack spacing={2} aria-busy="true" aria-label={t('loading.posts')}>
      {Array.from({ length: count }, (_, index) => (
        <PostCardSkeleton key={index} withMedia={index === 1} />
      ))}
    </Stack>
  );
}
